import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { Divider } from "../divider";
import { Icon } from "../icon";
import { SIZE_FONT } from "../utils/colors";
import { type UiPaletteKey, resolveUiPalette } from "../utils/uiPalette";
import type { MessageProps, MessageSize } from "./Message.types";

type StyleProps = {
  $bg: string;
  $color: string;
  $border: string;
  $size?: MessageSize;
};

const DEFAULT_TONE = {
  bg: "#f8f8f9",
  color: "rgba(0, 0, 0, 0.87)",
  border: "rgba(34, 36, 38, 0.22)",
};

const StyledMessage = styled.div<StyleProps>`
  position: relative;
  min-height: 1em;
  margin: 1em 0;
  background: ${({ $bg }) => $bg};
  padding: 1em 1.5em;
  line-height: 1.4285em;
  color: ${({ $color }) => $color};
  transition: opacity 0.1s ease;
  border-radius: 0.28571429rem;
  box-shadow: 0 0 0 1px ${({ $border }) => $border} inset;
  font-size: ${({ $size }) => ($size && SIZE_FONT[$size]) || "1em"};

  &:first-child {
    margin-top: 0;
  }

  ${Divider} {
    border-color: ${({ $border }) => $border};
  }

  ${Icon} {
    font-size: 1.1em;
    opacity: 0.8;
  }

  > ${Icon} {
    position: absolute;
    top: 7px;
    right: 6px;
  }
`;

const MessageComponent = ({
  color,
  size,
  children,
  className,
  style,
}: MessageProps) => {
  const theme = useTheme();
  const palette = color ? resolveUiPalette(theme, color as UiPaletteKey) : null;
  const tone = palette
    ? { bg: palette.bgSoft, color: palette.fg, border: palette.border }
    : DEFAULT_TONE;

  return (
    <StyledMessage
      className={cx(className)}
      style={style}
      role="status"
      $bg={tone.bg}
      $color={tone.color}
      $border={tone.border}
      $size={size}
    >
      {children}
    </StyledMessage>
  );
};

export const Message = styled(MessageComponent)``;
